import React, { useCallback, useRef, useMemo } from "react";
import { BehaviorSubject } from "rxjs";
import PropTypes from "prop-types";
import Article from "../../models/Article";
import Contributions from "../../../social/Contributions";
import ReferencedByUsers from "../ReferencedByUsers";
import FormattedText from "../FormattedText";
import SourceEntity from "../../models/SourceEntity";
import LikeButton from "../SocialFeatures/LikeButton";
import CommentButton from "../SocialFeatures/CommentButton";
import TagList from "../TagList";
import isPortal from "../../../../common/helpers/isPortal";
import { pluralize } from "../../../../common/helpers/helpers";
import styles from "./PublicationListItem.module.scss";

function findFirstParentWithTagName(node, tagName) {
  let parent = node;
  while (parent && parent.tagName !== "BODY") {
    if (parent.hasAttribute(tagName)) {
      return parent.getAttribute(tagName);
    }
    parent = parent.parentNode;
  }
  return "none";
}

function PublicationListItem({
  article,
  onSelect,
  sourceEntity,
  shouldShowAbstract,
  shouldShowTags,
  shouldShowContributions,
  filterTagSet,
}) {
  const highlighted$ = useRef(new BehaviorSubject(new Set())).current;
  const selected$ = useRef(new BehaviorSubject(new Set())).current;
  const hasAbstract = article.abstract.text;
  const slug = sourceEntity?.getReactionSlugForPub(article?.pubmedId);
  const isClinical = isPortal();

  const linkedBy = useMemo(
    () => (sourceEntity ? sourceEntity.getSourcesForArticle(article) : []),
    [sourceEntity, article]
  );

  const onSelectArticle = useCallback(
    (ev) => {
      const tab = findFirstParentWithTagName(ev.target, "data-tab");
      if (tab === "none") return;
      onSelect({ article, tab });
    },
    [onSelect, article]
  );

  return (
    <div onClick={onSelectArticle} className="tw-my-8 font-roboto">
      <div
        data-tab="abstract"
        className={`${styles.publicationCard} tw-rounded-lg tw-overflow-hidden hover:tw-border-blueGray-300 tw-relative pub-collapsed tw-cursor-pointer`}
      >
        {!shouldShowContributions && sourceEntity && !isClinical ? (
          <ReferencedByUsers
            limit={1}
            article={article}
            pubmedId={article.pubmedId}
            sourceEntity={sourceEntity}
          />
        ) : null}

        <div className="tw-relative tw-text-blueGray-600 hover:tw-text-blueGray-700 tw-my-2">
          <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mx-3">
            <div className="tw-flex-grow tw-flex-1 tw-min-w-96 tw-min-h-16">
              <div className="tw-w-full">
                {article.title.text ? (
                  <div className="tw-font-semibold zzz tw-cursor-pointer tw-text-base">
                    <FormattedText
                      highlighted$={highlighted$}
                      selected$={selected$}
                      domTree={article.title.domTree}
                    />
                  </div>
                ) : (
                  <div className="tw-text-sm tw-flex tw-items-center">
                    Not available
                    <div className="tw-mx-2 tw-flex-shrink-0 tw-w-1 tw-h-1 tw-bg-blueGray-400 tw-rounded-full" />
                    <a
                      target="_blank"
                      href={article.pubmedLink}
                      className="tw-inline-block"
                    >
                      {article.pubmedId}
                      <i className="fa fa-external-link tw-px-1" />
                    </a>
                  </div>
                )}
                <div className="tw-text-blueGray-500 tw-text-xs">
                  <div className="tw-flex tw-items-center">
                    {article.readableDate ? (
                      <div className="tw-flex-shrink-0">
                        {article.readableDate}
                      </div>
                    ) : null}

                    {article.journalName && article.readableDate ? (
                      <div className="tw-px-2 tw-flex-shrink-0">in</div>
                    ) : null}

                    {article.journalName ? (
                      <>
                        <div className="tw-min-w-0 tw-truncate">
                          {article.journal}
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className="tw-flex tw-items-center">
                    {article.mainAuthor ? (
                      <>
                        <div className="tw-flex-shrink-0">
                          {article.mainAuthor}
                        </div>
                        <div className="tw-mx-2 tw-flex-shrink-0 tw-w-1 tw-h-1 tw-bg-blueGray-400 tw-rounded-full" />
                      </>
                    ) : null}

                    {article.citationCount === null ? null : (
                      <>
                        <div className="tw-font-bold">
                          {article.citationCount}
                        </div>
                        <div className="tw-w-2" />
                        <div className="tw-truncate">
                          {pluralize(
                            article.citationCount,
                            "citation",
                            "citations"
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {linkedBy.length ? (
                    <div className="tw-truncate">
                      Linked by: {linkedBy.join(", ")}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {shouldShowAbstract ? (
              <div className="tw-flex-grow tw-flex-1 tw-min-w-96 tw-relative">
                <div className="tw-h-2 tw-absolute tw-top-0 tw-inset-x-0 tw-z-10 abstract-cuttof-t" />
                <div className={hasAbstract ? "tw-min-h-16 tw-flex-grow " : ""}>
                  <div className="tw-absolute tw-overflow-hidden tw-inset-0">
                    <div className="tw-absolute tw-z-0 tw-py-2 tw-inset-0 tw-text-justify tw-text-xs tw-opacity-90 tw-overflow-visible">
                      {hasAbstract ? (
                        <FormattedText
                          highlighted$={highlighted$}
                          selected$={selected$}
                          domTree={article.abstract.domTree}
                          isAutoScroll={true}
                        />
                      ) : (
                        <div className="tw-opacity-80">
                          Abstract not Available{" "}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="tw-absolute tw-bottom-0 tw-inset-x-0 abstract-cuttof-b" />
              </div>
            ) : null}
          </div>
        </div>
        {shouldShowTags ? (
          <div className="tw-flex tw-flex-wrap tw-items-stretch">
            <div className="tw-overflow-hidden tw-mx-2 tw-z-10 tw-flex-grow tw-pb-2">
              <div className="tw-text-xs tw-flex tw-flex-no-wrap">
                <TagList
                  article={article}
                  highlighted$={highlighted$}
                  selected$={selected$}
                  filterTagSet={filterTagSet}
                />
              </div>
            </div>
            <div className="tw-flex-grow tw-w-1/2 tw-min-w-96 tw-flex-shrink-0" />
            <div className="tw-absolute tw-right-0 tw-bottom-0 tw-w-8 tags-cuttof" />
          </div>
        ) : null}
        {shouldShowContributions && sourceEntity?.id && !isClinical ? (
          <div className="tw-mx-3">
            <Contributions
              shouldShowThread={false}
              article={article}
              pubmedId={article.pubmedId}
              showPlaceholderIfEmpty={false}
              sourceEntity={sourceEntity}
            />
          </div>
        ) : null}
      </div>

      <div className="tw-flex tw-ml-6">
        {slug && sourceEntity?.id && !isClinical ? (
          <div className="tw-text-blueGray-500 tw-text-xs tw-gap-4 tw-flex tw-pt-1">
            <div data-tab="none">
              <LikeButton
                sourceEntity={sourceEntity}
                subType="publications"
                subTypeId={article.pubmedId}
                slug={slug}
                className="tw-w-6 tw-h-6"
                size={16}
              />
            </div>

            <div className="tw-text-right" data-tab="comments">
              <CommentButton
                sourceEntity={sourceEntity}
                isActive={false}
                slug={slug}
                className="tw-w-6 tw-h-6"
                size={16}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

PublicationListItem.propTypes = {
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  onSelect: PropTypes.func.isRequired,
  article: PropTypes.instanceOf(Article).isRequired,
  shouldShowAbstract: PropTypes.bool,
  shouldShowTags: PropTypes.bool,
  shouldShowContributions: PropTypes.bool,
  filterTagSet: PropTypes.instanceOf(Set),
};

PublicationListItem.defaultProps = {
  sourceEntity: null,
  shouldShowAbstract: false,
  shouldShowTags: false,
  shouldShowContributions: false,
  filterTagSet: new Set(),
};

export default React.memo(PublicationListItem);
